import { ApiError } from "api/client";
import {
	changeTreeMemberRole,
	getTreeSharing,
	inviteToTree,
	removeTreeMember,
	revokeTreeInvitation,
	searchUsers
} from "api/sharing";
import { changeTreeVisibility, updateTree } from "api/trees";
import type { TreeDto, TreeInvitationDto, TreeMemberDto, TreeSharingDto, UserSummaryDto } from "api/types";
import { useBrand } from "hooks/useBrand";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { type ChangeEvent, type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";

import ViewIcon, { type ViewIconName } from "./ViewIcon";

import styles from "./ShareTreeDialog.module.css";

type ShareTreeDialogProps = {
	onClose: () => void;
	/** Copies the share link and shows the page toast; the dialog stays open. */
	onCopyLink: () => void;
	/** Receives the updated tree after visibility or comment toggles. */
	onTreeUpdated: (tree: TreeDto) => void;
	tree: TreeDto;
};

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

const ROLES = ["Viewer", "Editor", "Admin"];

type RoleCard = { descKey: string; icon: ViewIconName; nameKey: string };

const roleCards: RoleCard[] = [
	{ descKey: "shareDialog.roleViewerDesc", icon: "eye", nameKey: "shareDialog.roleViewer" },
	{ descKey: "shareDialog.roleEditorDesc", icon: "pencil", nameKey: "shareDialog.roleEditor" },
	{ descKey: "shareDialog.roleAdminDesc", icon: "shield", nameKey: "shareDialog.roleAdmin" }
];

/**
 * Collaboration modal: share the tree with a registered user found by email or username, manage
 * current collaborators, and flip the tree's public/comments settings. Admin-only (gated by caller).
 */
const ShareTreeDialog = ({ onClose, onCopyLink, onTreeUpdated, tree }: ShareTreeDialogProps) => {
	const { t } = useTranslation("homePage");
	const { brand } = useBrand();
	const { getAccessToken } = useSession();
	const dialogRef = useDialogFocus<HTMLDivElement>();
	const [sharing, setSharing] = useState<TreeSharingDto | null>(null);
	const [inviteQuery, setInviteQuery] = useState("");
	const [inviteRole, setInviteRole] = useState("Viewer");
	const [suggestions, setSuggestions] = useState<UserSummaryDto[]>([]);
	// Set when someone was just picked from the dropdown, so the effect doesn't re-open it.
	const skipNextSearch = useRef(false);
	const [isInviting, setIsInviting] = useState(false);
	const [isTogglingPublic, setIsTogglingPublic] = useState(false);
	const [isTogglingComments, setIsTogglingComments] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const loadSharing = useCallback(async (): Promise<void> => {
		const token = await getAccessToken();
		if (!token) {
			return;
		}

		try {
			setSharing(await getTreeSharing(tree.id, token));
		} catch (error) {
			setErrorMessage(error instanceof ApiError ? error.message : t("shareDialog.loadError"));
		}
	}, [getAccessToken, t, tree.id]);

	useEffect(() => {
		loadSharing().catch(() => undefined);
	}, [loadSharing]);

	const onQueryChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => setInviteQuery(event.target.value),
		[]
	);

	// Debounced people search on email or username; people who already have access are hidden.
	useEffect(() => {
		if (skipNextSearch.current) {
			skipNextSearch.current = false;

			return undefined;
		}

		const query = inviteQuery.trim();
		if (query.length < 3) {
			setSuggestions([]);

			return undefined;
		}

		let isCurrent = true;
		const timer = window.setTimeout(async (): Promise<void> => {
			const token = await getAccessToken();
			if (!token || !isCurrent) {
				return;
			}

			try {
				const found = await searchUsers(query, token);
				if (!isCurrent) {
					return;
				}

				const existingEmails = new Set(
					[
						...(sharing?.members ?? []).map((member: TreeMemberDto) => member.email),
						...(sharing?.invitations ?? []).map((invitation: TreeInvitationDto) => invitation.email)
					].map((email: string) => email.toLowerCase())
				);
				setSuggestions(found.filter((user: UserSummaryDto) => !existingEmails.has(user.email.toLowerCase())));
			} catch {
				// Autocomplete is best-effort; typing a full email still works.
			}
		}, 300);

		return () => {
			isCurrent = false;
			window.clearTimeout(timer);
		};
	}, [getAccessToken, inviteQuery, sharing]);

	// Picking someone fills the field with their email — the unambiguous half of the pair.
	const onSuggestionClick = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
		const email = event.currentTarget.dataset.email;
		if (!email) {
			return;
		}

		skipNextSearch.current = true;
		setInviteQuery(email);
		setSuggestions([]);
	}, []);
	const onRoleChange = useCallback(
		(event: ChangeEvent<HTMLSelectElement>): void => setInviteRole(event.target.value),
		[]
	);

	const onInviteSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			const recipient = inviteQuery.trim();
			if (!recipient || isInviting) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setErrorMessage("");
			setIsInviting(true);
			try {
				await inviteToTree(tree.id, recipient, inviteRole, token);
				setInviteQuery("");
				await loadSharing();
			} catch (error) {
				// "No Tree Tier account matches that email or username" arrives this way.
				setErrorMessage(error instanceof ApiError ? error.message : t("shareDialog.inviteError"));
			} finally {
				setIsInviting(false);
			}
		},
		[getAccessToken, inviteQuery, inviteRole, isInviting, loadSharing, t, tree.id]
	);

	const onMemberRoleChange = useCallback(
		async (event: ChangeEvent<HTMLSelectElement>): Promise<void> => {
			const userId = event.currentTarget.dataset.userId;
			const role = event.currentTarget.value;
			if (!userId) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setErrorMessage("");
			// Optimistic: the dropdown already shows the new value.
			setSharing((current: TreeSharingDto | null) =>
				current
					? {
							...current,
							members: current.members.map((member: TreeMemberDto) =>
								member.userId === userId ? { ...member, role } : member
							)
					  }
					: current
			);

			try {
				await changeTreeMemberRole(tree.id, userId, role, token);
			} catch (error) {
				setErrorMessage(error instanceof ApiError ? error.message : t("shareDialog.updateError"));
				await loadSharing();
			}
		},
		[getAccessToken, loadSharing, t, tree.id]
	);

	const onRemoveMember = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
			const userId = event.currentTarget.dataset.userId;
			if (!userId) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setErrorMessage("");
			setSharing((current: TreeSharingDto | null) =>
				current
					? {
							...current,
							members: current.members.filter((member: TreeMemberDto) => member.userId !== userId)
					  }
					: current
			);

			try {
				await removeTreeMember(tree.id, userId, token);
			} catch (error) {
				setErrorMessage(error instanceof ApiError ? error.message : t("shareDialog.updateError"));
				await loadSharing();
			}
		},
		[getAccessToken, loadSharing, t, tree.id]
	);

	const onRevokeInvitation = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
			const invitationId = event.currentTarget.dataset.invitationId;
			if (!invitationId) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setErrorMessage("");
			setSharing((current: TreeSharingDto | null) =>
				current
					? {
							...current,
							invitations: current.invitations.filter(
								(invitation: TreeInvitationDto) => invitation.id !== invitationId
							)
					  }
					: current
			);

			try {
				await revokeTreeInvitation(tree.id, invitationId, token);
			} catch (error) {
				setErrorMessage(error instanceof ApiError ? error.message : t("shareDialog.updateError"));
				await loadSharing();
			}
		},
		[getAccessToken, loadSharing, t, tree.id]
	);

	const onTogglePublic = useCallback(async (): Promise<void> => {
		if (isTogglingPublic) {
			return;
		}

		const token = await getAccessToken();
		if (!token) {
			return;
		}

		setErrorMessage("");
		setIsTogglingPublic(true);
		try {
			// Leaving Public falls back to Private, which the backend gates behind Pro.
			const next = tree.visibility === "Public" ? "Private" : "Public";
			onTreeUpdated(await changeTreeVisibility(tree.id, next, token));
		} catch (error) {
			setErrorMessage(error instanceof ApiError ? error.message : t("shareDialog.updateError"));
		} finally {
			setIsTogglingPublic(false);
		}
	}, [getAccessToken, isTogglingPublic, onTreeUpdated, t, tree.id, tree.visibility]);

	const onToggleComments = useCallback(async (): Promise<void> => {
		if (isTogglingComments) {
			return;
		}

		const token = await getAccessToken();
		if (!token) {
			return;
		}

		setErrorMessage("");
		setIsTogglingComments(true);
		try {
			// Omitting the cover keeps the existing one server-side.
			const saved = await updateTree(
				tree.id,
				{
					title: tree.title,
					description: tree.description?.trim() || undefined,
					commentsEnabled: !tree.commentsEnabled
				},
				token
			);
			onTreeUpdated(saved);
		} catch (error) {
			setErrorMessage(error instanceof ApiError ? error.message : t("shareDialog.updateError"));
		} finally {
			setIsTogglingComments(false);
		}
	}, [getAccessToken, isTogglingComments, onTreeUpdated, t, tree]);

	const isPublic = tree.visibility === "Public";

	return (
		<>
			<button aria-label={t("shareDialog.close")} className={styles.scrim} onClick={onClose} type="button" />
			<div
				ref={dialogRef}
				aria-label={t("shareDialog.title")}
				aria-modal="true"
				className={styles.dialog}
				role="dialog">
				<header className={styles.header}>
					<img alt="" className={styles.emblem} src={`${process.env.PUBLIC_URL}/assets/images/logo.png`} />
					<div className={styles.headerCopy}>
						<h2 className={styles.heading}>{t("shareDialog.title")}</h2>
						<p className={styles.subheading}>{t("shareDialog.subtitle", { brand })}</p>
					</div>
					<button
						aria-label={t("shareDialog.close")}
						className={styles.closeButton}
						onClick={onClose}
						type="button">
						<ViewIcon name="close" />
					</button>
				</header>

				<div className={styles.body}>
					<div className={styles.mainColumn}>
						<form className={styles.inviteRow} onSubmit={onInviteSubmit}>
							<div className={styles.emailField}>
								<input
									aria-label={t("shareDialog.recipientLabel")}
									autoComplete="off"
									className={styles.emailInput}
									onChange={onQueryChange}
									placeholder={t("shareDialog.recipientPlaceholder")}
									type="text"
									value={inviteQuery}
								/>
								{suggestions.length > 0 && (
									<div className={styles.suggestions} role="listbox">
										{suggestions.map((user: UserSummaryDto) => (
											<button
												key={user.id}
												aria-selected={false}
												className={styles.suggestionItem}
												data-email={user.email}
												onClick={onSuggestionClick}
												role="option"
												type="button">
												<span className={styles.suggestionAvatar}>
													{user.profileImageUrl ? (
														<img
															alt=""
															referrerPolicy="no-referrer"
															src={user.profileImageUrl}
														/>
													) : (
														getInitials(user.fullName)
													)}
												</span>
												<span className={styles.suggestionBody}>
													<span className={styles.suggestionName}>{user.fullName}</span>
													<span className={styles.suggestionEmail}>
														{t("shareDialog.suggestionMeta", {
															email: user.email,
															userName: user.userName
														})}
													</span>
												</span>
											</button>
										))}
									</div>
								)}
							</div>
							<select
								aria-label={t("shareDialog.roleLabel")}
								className={styles.roleSelect}
								onChange={onRoleChange}
								value={inviteRole}>
								{ROLES.map((role: string) => (
									<option key={role} value={role}>
										{role}
									</option>
								))}
							</select>
							<button
								className={styles.sendInviteButton}
								disabled={isInviting || inviteQuery.trim().length === 0}
								type="submit">
								{isInviting ? t("shareDialog.sending") : t("shareDialog.sendInvite")}
							</button>
						</form>

						<div className={styles.roleCards}>
							{roleCards.map((card: RoleCard) => (
								<div key={card.nameKey} className={styles.roleCard}>
									<span className={styles.roleCardIcon}>
										<ViewIcon name={card.icon} />
									</span>
									<span className={styles.roleCardBody}>
										<span className={styles.roleCardName}>{t(card.nameKey)}</span>
										<span className={styles.roleCardDesc}>{t(card.descKey)}</span>
									</span>
								</div>
							))}
						</div>

						{errorMessage && <p className={styles.error}>{errorMessage}</p>}

						<h3 className={styles.sectionTitle}>{t("shareDialog.collaborators")}</h3>
						<div className={styles.memberList}>
							{sharing?.members.map((member: TreeMemberDto) => (
								<div key={member.userId} className={styles.memberRow}>
									<span className={styles.memberAvatar}>
										{member.profileImageUrl ? (
											<img alt="" referrerPolicy="no-referrer" src={member.profileImageUrl} />
										) : (
											getInitials(member.fullName)
										)}
									</span>
									<span className={styles.memberIdentity}>
										<span className={styles.memberName}>{member.fullName}</span>
										<span className={styles.memberEmail}>{member.email}</span>
									</span>
									{member.isOwner && (
										<span className={styles.ownerBadge}>{t("view.ownerBadge")}</span>
									)}
									{!member.isOwner && member.status === "Pending" && (
										<>
											<span className={styles.pendingRole}>
												{t("shareDialog.pendingBadge", { role: member.role })}
											</span>
											<button
												aria-label={t("shareDialog.revokeInvite")}
												className={styles.removeButton}
												data-user-id={member.userId}
												onClick={onRemoveMember}
												title={t("shareDialog.revokeInvite")}
												type="button">
												<ViewIcon name="trash" />
											</button>
										</>
									)}
									{!member.isOwner && member.status !== "Pending" && (
										<>
											<select
												aria-label={t("shareDialog.roleLabel")}
												className={styles.memberRoleSelect}
												data-user-id={member.userId}
												onChange={onMemberRoleChange}
												value={member.role}>
												{ROLES.map((role: string) => (
													<option key={role} value={role}>
														{role}
													</option>
												))}
											</select>
											<button
												aria-label={t("shareDialog.removeMember")}
												className={styles.removeButton}
												data-user-id={member.userId}
												onClick={onRemoveMember}
												title={t("shareDialog.removeMember")}
												type="button">
												<ViewIcon name="trash" />
											</button>
										</>
									)}
								</div>
							))}

							{sharing?.invitations.map((invitation: TreeInvitationDto) => (
								<div key={invitation.id} className={styles.memberRow}>
									<span className={styles.memberAvatar}>
										<ViewIcon name="mail" />
									</span>
									<span className={styles.memberIdentity}>
										<span className={styles.memberName}>{invitation.email}</span>
										<span className={styles.memberEmail}>{t("shareDialog.invitePending")}</span>
									</span>
									<span className={styles.pendingRole}>{invitation.role}</span>
									<button
										aria-label={t("shareDialog.revokeInvite")}
										className={styles.removeButton}
										data-invitation-id={invitation.id}
										onClick={onRevokeInvitation}
										title={t("shareDialog.revokeInvite")}
										type="button">
										<ViewIcon name="trash" />
									</button>
								</div>
							))}

							{sharing && sharing.members.length <= 1 && sharing.invitations.length === 0 && (
								<p className={styles.emptyState}>{t("shareDialog.noCollaborators")}</p>
							)}
						</div>
					</div>

					<aside className={styles.settingsColumn}>
						<div className={styles.settingRow}>
							<span className={styles.settingIcon}>
								<ViewIcon name="globe" />
							</span>
							<span className={styles.settingBody}>
								<span className={styles.settingName}>{t("shareDialog.makePublic")}</span>
								<span className={styles.settingDesc}>{t("shareDialog.makePublicDesc")}</span>
							</span>
							<button
								aria-checked={isPublic}
								aria-label={t("shareDialog.makePublic")}
								className={isPublic ? styles.switchOn : styles.switchOff}
								disabled={isTogglingPublic}
								onClick={onTogglePublic}
								role="switch"
								type="button">
								<span className={styles.switchThumb} />
							</button>
						</div>

						<div className={styles.settingDivider} />

						<div className={styles.settingRow}>
							<span className={styles.settingIcon}>
								<ViewIcon name="comment" />
							</span>
							<span className={styles.settingBody}>
								<span className={styles.settingName}>{t("shareDialog.enableComments")}</span>
								<span className={styles.settingDesc}>{t("shareDialog.enableCommentsDesc")}</span>
							</span>
							<button
								aria-checked={tree.commentsEnabled}
								aria-label={t("shareDialog.enableComments")}
								className={tree.commentsEnabled ? styles.switchOn : styles.switchOff}
								disabled={isTogglingComments}
								onClick={onToggleComments}
								role="switch"
								type="button">
								<span className={styles.switchThumb} />
							</button>
						</div>
					</aside>
				</div>

				<footer className={styles.footer}>
					<button className={styles.copyLinkButton} onClick={onCopyLink} type="button">
						<ViewIcon name="share" />
						<span>{t("shareDialog.copyLink")}</span>
					</button>
					<button className={styles.doneButton} onClick={onClose} type="button">
						{t("shareDialog.done")}
					</button>
				</footer>
			</div>
		</>
	);
};

export default ShareTreeDialog;
